import { useEffect, useRef, useState } from 'react';
import { REFERRALS_ENABLED } from '../config/featureFlags';
import type { CompanionMessage } from '../hooks/useCompanionMessage';
import { DISPLAY_NAME_MAX_LENGTH } from '@shared/domain/displayNameValidation';
import { NICKNAME_CONFIRM_VALUE } from '@shared/domain/companionQuestions';
import { DEFAULT_ACKNOWLEDGMENT } from '@shared/domain/companionMessages';
import './CompanionMessageModal.css';
import { useT } from '../i18n/i18nContext';

interface CompanionMessageModalProps {
  companion: CompanionMessage;
}

/** Value sent back for a CTA prompt when the player follows the link vs. declines it. */
const CTA_ANSWER_YES = 'yes';
const CTA_ANSWER_NOT_YET = 'not_yet';

/** `id` for the spoken message paragraph, referenced by the dialog's `aria-describedby`. */
const COMPANION_MESSAGE_TEXT_ID = 'companion-popup-message-text';

/** Selector for focusable elements considered when trapping focus inside the dialog. */
const FOCUSABLE_SELECTOR = 'button:not([disabled]), a[href], input:not([disabled])';

/** External destinations for community CTA prompts, keyed by `CompanionPrompt.ctaUrlKey`. */
const X_PROFILE_URL = 'https://x.com/suikamongame';
const TELEGRAM_COMMUNITY_URL = 'https://t.me/suikamongame';
// The "have you told a friend?" nudge. While referrals are on it points at the referral
// page; while they are off that route redirects home, so it points at the public adopt page
// instead — the nudge is a community prompt about spreading the word, and it stays useful
// without a referral program behind it. See shared/domain/featureFlags.ts.
const REFERRAL_PAGE_URL = REFERRALS_ENABLED ? '/referrals' : '/adopt';

const CTA_URLS: Record<NonNullable<CompanionMessage['ctaUrlKey']>, string> = {
  x: X_PROFILE_URL,
  telegram: TELEGRAM_COMMUNITY_URL,
  referral: REFERRAL_PAGE_URL,
};

/**
 * Speech-bubble popup that presents a companion greeting as if the Suikamon itself is
 * talking to the owner: the pet's stage image beside a chat bubble with its in-character
 * line. Content and dedupe are decided by {@link useCompanionMessage}; this component is
 * purely presentational and closes via `companion.dismiss`.
 *
 * Renders one of three modes based on `companion.kind`:
 *  - `statement` — a greeting/observation/recap line with a single dismiss button.
 *  - `question` — an in-character prompt, answered either via tappable choice chips
 *    (`promptInput` is `{choices}`), a short text field (`promptInput === 'text'`), or
 *    the nickname confirm variant (`promptInput === 'nickname_confirm'`, shown when the
 *    player already has a display name): Yep keeps the name, Change reveals the same
 *    text-input UI as the open nickname prompt.
 *  - `cta` — a community nudge with a primary link (opens in a new tab and records
 *    `answer('yes')`) plus a secondary "not yet" dismiss (`answer('not_yet')`).
 */
export function CompanionMessageModal({ companion }: CompanionMessageModalProps) {
  const t = useT();
  const { petName, imageUrl, personalityLabel, message, kind, acknowledgeLabel, promptInput, ctaUrlKey, answer, dismiss } =
    companion;
  const [textValue, setTextValue] = useState('');
  const [changingNickname, setChangingNickname] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Close on Escape, matching standard dialog behavior.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dismiss]);

  // On open: move focus into the dialog (primary control for the current mode, marked via
  // `data-autofocus`), and restore focus to whatever had it before on close/unmount.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const card = cardRef.current;
    const target =
      card?.querySelector<HTMLElement>('[data-autofocus]') ??
      card?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    target?.focus();

    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
     
  }, []);

  // Focus trap: keep Tab/Shift+Tab cycling within the dialog while it's open.
  const handleTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const card = cardRef.current;
    if (!card) return;
    const focusable = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement;

    if (e.shiftKey) {
      if (current === first || !card.contains(current)) {
        e.preventDefault();
        last.focus();
      }
    } else if (current === last || !card.contains(current)) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleChoice = (choiceId: string) => {
    answer?.(choiceId);
  };

  const handleTextSubmit = () => {
    const trimmed = textValue.trim();
    if (!trimmed) return;
    answer?.(trimmed);
  };

  const handleNicknameKeep = () => {
    answer?.(NICKNAME_CONFIRM_VALUE);
  };

  const handleNicknameChangeRequest = () => {
    setChangingNickname(true);
  };

  const handleCtaClick = () => {
    answer?.(CTA_ANSWER_YES);
  };

  const handleCtaDecline = () => {
    answer?.(CTA_ANSWER_NOT_YET);
  };

  const ctaLabel =
    (typeof promptInput === 'object' ? promptInput.choices[0]?.label : undefined) ?? t('care.companion.ctaDefault');

  return (
    <div
      className="companion-popup-overlay"
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
      aria-label={t('care.companion.saysHello', { petName })}
      aria-describedby={message ? COMPANION_MESSAGE_TEXT_ID : undefined}
    >
      <div
        className="companion-popup-card"
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleTrapKeyDown}
      >
        <button className="companion-popup-close" onClick={dismiss} aria-label={t('common.action.close')}>
          ✕
        </button>

        <div className="companion-popup-avatar">
          <img src={imageUrl} alt={petName} className="companion-popup-image" />
        </div>

        <div className="companion-popup-bubble">
          <div className="companion-popup-name">
            {petName}
            <span className="companion-popup-persona">{personalityLabel}</span>
          </div>
          <p className="companion-popup-text" id={COMPANION_MESSAGE_TEXT_ID}>
            {message}
          </p>

          {kind === 'statement' && (
            <button className="companion-popup-cta" onClick={dismiss} data-autofocus>
              {acknowledgeLabel ?? DEFAULT_ACKNOWLEDGMENT}
            </button>
          )}

          {kind === 'question' && typeof promptInput === 'object' && (
            <div className="companion-popup-chips">
              {promptInput.choices.map((choice, index) => (
                <button
                  key={choice.id}
                  type="button"
                  className="companion-popup-chip"
                  onClick={() => handleChoice(choice.id)}
                  data-autofocus={index === 0 ? true : undefined}
                >
                  {choice.label}
                </button>
              ))}
            </div>
          )}

          {kind === 'question' && promptInput === 'text' && (
            <div className="companion-popup-answer-form">
              <input
                type="text"
                className="companion-popup-input"
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                placeholder={t('care.companion.answerPlaceholder')}
                aria-label={t('care.companion.answerLabel')}
                data-autofocus
              />
              <button type="button" className="companion-popup-cta" onClick={handleTextSubmit}>
                {t('common.action.submit')}
              </button>
            </div>
          )}

          {kind === 'question' && promptInput === 'nickname_confirm' && !changingNickname && (
            <div className="companion-popup-chips">
              <button
                type="button"
                className="companion-popup-chip"
                onClick={handleNicknameKeep}
                data-autofocus
              >
                {t('care.companion.keepNickname')}
              </button>
              <button type="button" className="companion-popup-chip" onClick={handleNicknameChangeRequest}>
                {t('care.companion.changeNickname')}
              </button>
            </div>
          )}

          {kind === 'question' && promptInput === 'nickname_confirm' && changingNickname && (
            <div className="companion-popup-answer-form">
              <input
                type="text"
                className="companion-popup-input"
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                placeholder={t('care.companion.nicknamePlaceholder')}
                aria-label={t('care.companion.nicknameLabel')}
                data-autofocus
              />
              <button type="button" className="companion-popup-cta" onClick={handleTextSubmit}>
                {t('common.action.submit')}
              </button>
            </div>
          )}

          {kind === 'cta' && ctaUrlKey && (
            <div className="companion-popup-cta-row">
              <a
                href={CTA_URLS[ctaUrlKey]}
                target="_blank"
                rel="noopener noreferrer"
                className="companion-popup-cta"
                onClick={handleCtaClick}
                data-autofocus
                aria-label={t('care.companion.opensInNewTab', { label: ctaLabel })}
              >
                {ctaLabel}{' '}
                <span aria-hidden="true">↗</span>
              </a>
              <button type="button" className="companion-popup-secondary" onClick={handleCtaDecline}>
                {t('care.companion.notYet')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
